import React from "react";
import {
  calculateFusionWeightedRiskScore,
  getEligibleFusionStrategies,
  getFusionAllocationDisplayName,
  getPrimaryRegistrationValue,
  isIspOnlyProgram,
  isPrivateClientStrategyAlias,
  shouldForceTaxableForRegistration,
} from "../../utils/proposalUtils";
import {
  resolveFusionSubFees,
  validateManagementFeeTable,
} from "./fusionFeeUtils";
import {
  IspRegistrationOptions,
  isIspRegistrationAvailable,
} from "./ispRegistrationOptions";

const getTaxSensitivityOptions = (proposalType, forceTaxable = false) => {
  const options = [];
  if (
    Array.isArray(proposalType?.taxableBonds) &&
    proposalType.taxableBonds.length > 0
  ) {
    options.push("Taxable");
  }
  if (
    !forceTaxable &&
    Array.isArray(proposalType?.municipalBonds) &&
    proposalType.municipalBonds.length > 0
  ) {
    options.push("Municipal");
  }
  return options;
};

const getRiskToleranceOptions = (proposalType, taxSensitivity) => {
  if (taxSensitivity === "Municipal") {
    return proposalType?.municipalBonds || [];
  }
  if (taxSensitivity === "Taxable") {
    return proposalType?.taxableBonds || [];
  }
  return [
    ...(proposalType?.taxableBonds || []),
    ...(proposalType?.municipalBonds || []),
  ];
};

const FusionAllocationEditor = ({
  fusionAllocations,
  lists,
  onFusionAllocationsChange,
  bondType,
  registrations,
  fusionRegistrationType,
  jointClients,
  selectedManagementFeeTierKey,
  fusionSubFees,
  fusionAllocationLimitErrors,
  fusionAllocationRiskValidation,
}) => {
  const allocations = fusionAllocations || [];
  const isFusionMultiRegistration = fusionRegistrationType === "Multi";
  const accountOwnerOptions = (jointClients || [])
    .map(
      (client) =>
        client?.clientName ||
        [client?.firstName, client?.middleName, client?.lastName]
          .filter(Boolean)
          .join(" "),
    )
    .filter(Boolean);
  const primaryRegistrationValue = getPrimaryRegistrationValue(registrations);
  const forceTaxable = shouldForceTaxableForRegistration(
    primaryRegistrationValue,
    lists?.registrationTypes || [],
  );
  const preferredTaxSensitivity = forceTaxable
    ? "Taxable"
    : bondType || "Taxable";
  const proposalTypes = lists?.proposalTypes || [];
  const availableStrategyOptions = getEligibleFusionStrategies(proposalTypes, {
    allowIspOnly: true,
  })
    .filter(
      (proposalType) =>
        isPrivateClientStrategyAlias(proposalType?.name, proposalTypes) ||
        getTaxSensitivityOptions(proposalType, forceTaxable).length > 0,
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  const proposalTypeByName = new Map(
    proposalTypes.map((proposalType) => [proposalType.name, proposalType]),
  );

  const total = allocations.reduce(
    (sum, allocation) => sum + (parseFloat(allocation.percentage) || 0),
    0,
  );
  const isComplete = Math.abs(total - 100) < 0.01 && allocations.length > 0;
  const weightedRiskScore = calculateFusionWeightedRiskScore(
    allocations,
    proposalTypes,
  );
  const hasWeightedRiskScore = weightedRiskScore.score !== null;
  const weightedRiskScoreText = hasWeightedRiskScore
    ? `${(Math.round(weightedRiskScore.score * 100) / 100).toFixed(2)} (${weightedRiskScore.category})`
    : "N/A";
  const allocationRiskMessage =
    fusionAllocationRiskValidation?.isValid === false
      ? fusionAllocationRiskValidation.message
      : "";
  const maxFusionAllocations = 8;
  const isAtMaxAllocations = allocations.length >= maxFusionAllocations;
  const enrichAllocation = (allocation) => ({
    ...allocation,
    displayName: getFusionAllocationDisplayName(allocation, proposalTypes),
  });
  const handleAdd = () => {
    if (isAtMaxAllocations) return;
    onFusionAllocationsChange([
      ...allocations,
      {
        strategy: "",
        riskTolerance: "",
        taxSensitivity: "",
        registration: "",
        accountOwner: "",
        displayName: "",
        percentage: "",
      },
    ]);
  };
  const handleRemove = (index) =>
    onFusionAllocationsChange(
      allocations.filter((_, allocationIndex) => allocationIndex !== index),
    );
  const handleChange = (index, field, value) =>
    onFusionAllocationsChange(
      allocations.map((allocation, allocationIndex) =>
        allocationIndex === index
          ? enrichAllocation({ ...allocation, [field]: value })
          : allocation,
      ),
    );
  const handleStrategyChange = (index, strategyName) => {
    const proposalType = proposalTypeByName.get(strategyName);
    const taxOptions = getTaxSensitivityOptions(proposalType, forceTaxable);
    const taxSensitivity = isPrivateClientStrategyAlias(
      strategyName,
      proposalTypes,
    )
      ? ""
      : taxOptions.includes(preferredTaxSensitivity)
      ? preferredTaxSensitivity
      : taxOptions[0] || "";
    const riskOptions = getRiskToleranceOptions(proposalType, taxSensitivity);
    const current = allocations[index] || {};
    onFusionAllocationsChange(
      allocations.map((allocation, allocationIndex) =>
        allocationIndex === index
          ? enrichAllocation({
              ...current,
              strategy: strategyName,
              taxSensitivity,
              riskTolerance: riskOptions.length === 1 ? riskOptions[0] : "",
            })
          : allocation,
      ),
    );
  };
  const handleTaxSensitivityChange = (index, taxSensitivity) => {
    const current = allocations[index] || {};
    const proposalType = proposalTypeByName.get(current.strategy);
    const riskOptions = getRiskToleranceOptions(proposalType, taxSensitivity);
    const riskTolerance = riskOptions.includes(current.riskTolerance)
      ? current.riskTolerance
      : "";
    onFusionAllocationsChange(
      allocations.map((allocation, allocationIndex) =>
        allocationIndex === index
          ? enrichAllocation({
              ...current,
              taxSensitivity,
              riskTolerance,
            })
          : allocation,
      ),
    );
  };

  return (
    <div className="form-group mt-3" data-testid="fusion-allocation-editor">
      <label>
        <strong>Fusion Sub-Strategy Allocations</strong>
      </label>
      <small className="form-text text-muted mb-2">
        Select the investment programs within Fusion and assign percentage
        allocations totaling 100%.
      </small>

      {allocations.map((allocation, index) => {
        const feeTable = resolveFusionSubFees(
          fusionSubFees,
          allocation.strategy,
          selectedManagementFeeTierKey,
        );
        const feeValidation = validateManagementFeeTable(feeTable);
        const hasFeeData = !!(
          selectedManagementFeeTierKey &&
          allocation.strategy &&
          feeValidation.isComplete
        );
        const limitError = fusionAllocationLimitErrors?.[index] || null;
        const rowComplete =
          selectedManagementFeeTierKey &&
          allocation.strategy &&
          allocation.riskTolerance &&
          (isPrivateClientStrategyAlias(
            allocation.strategy,
            proposalTypes,
          ) ||
            allocation.taxSensitivity) &&
          (!isFusionMultiRegistration ||
            (allocation.registration &&
              allocation.accountOwner &&
              isIspRegistrationAvailable(
                allocation.registration,
                lists?.registrationTypes || [],
              ))) &&
          parseFloat(allocation.percentage) > 0 &&
          hasFeeData &&
          !limitError;
        const selectedProposalType = proposalTypeByName.get(allocation.strategy);
        const taxSensitivityDisabled =
          isPrivateClientStrategyAlias(
            allocation.strategy,
            proposalTypes,
          );
        const taxSensitivityOptions = getTaxSensitivityOptions(
          selectedProposalType,
          forceTaxable,
        );
        const riskToleranceOptions = getRiskToleranceOptions(
          selectedProposalType,
          taxSensitivityDisabled ? "" : allocation.taxSensitivity,
        );
        const strategyOptions = [...availableStrategyOptions];
        if (
          allocation.strategy &&
          !strategyOptions.some(
            (proposalType) => proposalType.name === allocation.strategy,
          )
        ) {
          strategyOptions.unshift({ name: allocation.strategy });
        }
        const allocationDisplayName =
          taxSensitivityDisabled || !allocation.displayName
            ? getFusionAllocationDisplayName(
                {
                  ...allocation,
                  taxSensitivity: taxSensitivityDisabled
                    ? ""
                    : allocation.taxSensitivity,
                },
                proposalTypes,
              )
            : allocation.displayName;

        return (
          <div
            key={index}
            className="mb-2"
            style={{
              borderLeft: `5px solid ${rowComplete ? "green" : "#ced4da"}`,
              paddingLeft: "12px",
            }}
          >
            <div className="form-row align-items-end">
              <div className="form-group col-lg-3 mb-2">
                <label htmlFor={`fusionStrategy${index}`}>Strategy</label>
                <select
                  id={`fusionStrategy${index}`}
                  className="form-control"
                  value={allocation.strategy || ""}
                  onChange={(event) =>
                    handleStrategyChange(index, event.target.value)
                  }
                >
                  <option value="">Select Strategy</option>
                  {strategyOptions.map((proposalType) => (
                    <option key={proposalType.name} value={proposalType.name}>
                      {proposalType.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group col-lg-3 mb-2">
                <label htmlFor={`fusionRiskTolerance${index}`}>
                  Risk Tolerance
                </label>
                <select
                  id={`fusionRiskTolerance${index}`}
                  className="form-control"
                  value={allocation.riskTolerance || ""}
                  disabled={
                    !allocation.strategy || riskToleranceOptions.length === 0
                  }
                  onChange={(event) =>
                    handleChange(index, "riskTolerance", event.target.value)
                  }
                >
                  <option value="">Select Risk</option>
                  {riskToleranceOptions.map((riskTolerance) => (
                    <option key={riskTolerance} value={riskTolerance}>
                      {riskTolerance}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group col-lg-3 mb-2">
                <label htmlFor={`fusionTaxSensitivity${index}`}>
                  Tax Sensitivity
                </label>
                <select
                  id={`fusionTaxSensitivity${index}`}
                  className="form-control"
                  value={
                    taxSensitivityDisabled
                      ? ""
                      : allocation.taxSensitivity || ""
                  }
                  disabled={
                    !allocation.strategy ||
                    taxSensitivityDisabled ||
                    taxSensitivityOptions.length === 0
                  }
                  onChange={(event) =>
                    handleTaxSensitivityChange(index, event.target.value)
                  }
                >
                  <option value="">
                    {taxSensitivityDisabled
                      ? "See custom proposal"
                      : "Select Tax"}
                  </option>
                  {taxSensitivityOptions.map((taxSensitivity) => (
                    <option key={taxSensitivity} value={taxSensitivity}>
                      {taxSensitivity}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group col-lg-2 mb-2">
                <label htmlFor={`fusionWeight${index}`}>Weight</label>
                <div className="input-group">
                  <input
                    id={`fusionWeight${index}`}
                    type="number"
                    className="form-control"
                    value={allocation.percentage}
                    min="0"
                    max="100"
                    step="1"
                    placeholder="0"
                    onChange={(event) => {
                      const value = event.target.value;
                      if (
                        value === "" ||
                        (/^(?:0|[1-9]\d*)$/.test(value) && Number(value) <= 100)
                      ) {
                        handleChange(index, "percentage", value);
                      }
                    }}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">%</span>
                  </div>
                </div>
              </div>
              <div className="form-group col-lg-1 mb-2">
                <button
                  type="button"
                  className="btn btn-danger"
                  style={{ padding: "6px 8px", marginTop: "31px" }}
                  onClick={() => handleRemove(index)}
                  title="Remove"
                >
                  <i className="fa fa-trash" style={{ fontSize: "12px" }} />
                </button>
              </div>
            </div>
            {allocationDisplayName && (
              <div
                className="text-muted"
                style={{ fontSize: "12px", marginTop: "-2px" }}
              >
                Allocation Model: {allocationDisplayName}
              </div>
            )}
            {isFusionMultiRegistration && (
              <div className="form-row mt-2">
                <div className="form-group col-md-4 mb-2">
                  <label htmlFor={`fusionAccountOwner${index}`}>
                    Account Owner
                  </label>
                  <select
                    id={`fusionAccountOwner${index}`}
                    className="form-control"
                    value={allocation.accountOwner || ""}
                    onChange={(event) =>
                      handleChange(index, "accountOwner", event.target.value)
                    }
                  >
                    <option value="">Select Account Owner</option>
                    {accountOwnerOptions.map((owner) => (
                      <option key={owner} value={owner}>
                        {owner}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group col-md-4 mb-2">
                  <label htmlFor={`fusionRegistration${index}`}>
                    Registration
                  </label>
                  <select
                    id={`fusionRegistration${index}`}
                    className="form-control"
                    value={allocation.registration || ""}
                    onChange={(event) =>
                      handleChange(index, "registration", event.target.value)
                    }
                  >
                    <IspRegistrationOptions
                      registrationTypes={lists?.registrationTypes || []}
                      registrationCategoryTypes={
                        lists?.registrationCategoryTypes
                      }
                      selectedValue={allocation.registration}
                    />
                  </select>
                  {allocation.registration &&
                    !isIspRegistrationAvailable(
                      allocation.registration,
                      lists?.registrationTypes || [],
                    ) && (
                      <small className="form-text text-danger" role="alert">
                        The restored Registration is unavailable. Select an
                        available Registration.
                      </small>
                    )}
                </div>
              </div>
            )}
            {limitError && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#dc3545",
                  marginTop: "4px",
                }}
              >
                {limitError}
              </div>
            )}
            {allocation.strategy &&
              isIspOnlyProgram(allocation.strategy, lists?.proposalTypes) && (
                <div
                  role="alert"
                  style={{
                    fontSize: "12px",
                    color: "#b8860b",
                    marginTop: "4px",
                  }}
                >
                  {allocation.strategy} is an ISP-only strategy. This ISP can be
                  generated, but it requires a custom proposal — a generated
                  proposal cannot be created from it.
                </div>
              )}
          </div>
        );
      })}

      <div className="d-flex align-items-center mt-2 mb-3">
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handleAdd}
          disabled={isAtMaxAllocations}
          title={
            isAtMaxAllocations
              ? `Maximum of ${maxFusionAllocations} sub-strategies reached`
              : undefined
          }
        >
          + Add Sub-Strategy
        </button>
        <span className="ml-3 text-muted" style={{ fontSize: "13px" }}>
          ({allocations.length}/{maxFusionAllocations})
        </span>
        {allocations.length > 0 && (
          <span
            className="ml-3"
            style={{
              fontWeight: "bold",
              color: isComplete ? "#28a745" : "#dc3545",
            }}
          >
            Total: {total.toFixed(0)}%
          </span>
        )}
      </div>

      {allocations.length > 0 && (
        <div
          className="mb-3"
          style={{
            fontSize: "13px",
            color: hasWeightedRiskScore ? "#495057" : "#6c757d",
            fontWeight: 600,
          }}
        >
          Weighted Risk Score: {weightedRiskScoreText}
          {weightedRiskScore.hasMissingScore && (
            <div
              style={{
                color: "#dc3545",
                fontWeight: 400,
                marginTop: "2px",
              }}
            >
              Risk score data is missing for one or more selected sub-strategies.
            </div>
          )}
        </div>
      )}

      {allocationRiskMessage && (
        <div
          className="alert alert-danger mb-3"
          role="alert"
          data-testid="fusion-allocation-risk-warning"
        >
          {allocationRiskMessage}
        </div>
      )}
    </div>
  );
};

export default FusionAllocationEditor;
